/**
 * Enable Account Step Component
 * Uses same pattern as CreateAccount with StepContent
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import useFormValidation from '../../hooks/useFormValidation';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import StepValidationService from '../../services/stepValidationService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

interface EnableAccountStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
  onSkip: (currentStep: string, nextStep: string) => void;
}

/**
 * EnableAccountStep component for handling account enable configuration
 */
function EnableAccountStep({
  message,
  onSkip,
  onFormSubmit,
}: EnableAccountStepProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Form validation hook
  const { validateForm } = useFormValidation();

  // Get persisted form data from Redux
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.EnableAccount
  );

  // Check if in edit mode
  const isInEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const skippedSteps = useSelector(
    (state: RootState) => state.copilot.navigation.skippedSteps ?? []
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');
  const isStepCompletedOrSkipped =
    isCompletedCheck.includes('EnableAccount') ||
    skippedSteps.includes('EnableAccount');
  const validateAndContinue = useCallback(async () => {
    setValidationError(null);

    // Use centralized validation service
    const validation = await StepValidationService.validateStep(
      'EnableAccount',
      persistedFormData,
      validateForm
    );

    if (validation.isValid) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: 'EnableAccount',
          formData: persistedFormData,
        })
      );

      onFormSubmit('EnableAccount', persistedFormData, validation.isValid);
    } else {
      setValidationError(validation.errors[0] || 'Validation failed');
    }

    // In edit mode, don't collapse form after successful submission
    return validation.isValid && !isInEditMode;
  }, [dispatch, persistedFormData, onFormSubmit, isInEditMode, validateForm]);

  const handleSkipStep = () => {
    onSkip('EnableAccount', 'DisableAccount');
  };

  return (
    <ResponseWithForm
      responseText={
        !isInEditMode
          ? FORM_RESPONSE_TEXTS.ENABLE_ACCOUNT
          : FORM_RESPONSE_TEXTS.ENABLE_ACCOUNT_EDIT
      }>
      <ExpandableForm
        stepName={message.stepName ?? 'EnableAccount'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}
        onSecondory={handleSkipStep}
        secondryButtonLabel={isInEditMode ? undefined : 'Skip'}
        isSecondaryDisabled={isStepCompletedOrSkipped}>
        <StepRenderer stepName='EnableAccount' />
      </ExpandableForm>
      {validationError && <ErrorBanner message={validationError} />}
    </ResponseWithForm>
  );
}

export default EnableAccountStep;
